import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Menu, 
  X, 
  ArrowRight, 
  Workflow,
  Calendar,
  LayoutDashboard,
  Users,
  Bot,
  MessageSquare,
  Wrench,
  Globe,
  CheckCircle2,
  ExternalLink,
  Zap,
  Shield,
  Clock,
  Send,
  Mail,
  Phone,
  ChevronRight,
  Play,
  Database,
  Settings,
  Bell
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo component - Updated to circular version
const Logo = () => (
  <img 
    src="/mushonga_logo_circular.png"
    alt="Mushonga Systems"
    className="h-10 w-auto"
  />
);

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Services', href: '#services' },
    { name: 'Case Studies', href: '#case-studies' },
    { name: 'Systems', href: '#systems' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass' : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="section-container">
        <nav className="flex items-center justify-between py-4">
          <a href="#" className="flex items-center gap-3" data-testid="logo-link">
            <Logo />
            <span className="text-white font-bold text-lg hidden sm:block">
              Mushonga Systems
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-gray-400 hover:text-white transition-colors font-medium text-sm"
                data-testid={`nav-${link.name.toLowerCase().replaceAll(' ', '-')}`}
              >
                {link.name}
              </a>
            ))}
          </div>

          <a
            href="#contact"
            className="hidden md:flex btn-primary"
            data-testid="header-cta"
          >
            Get Consultation
          </a>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10 animate-fadeIn">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-3 text-gray-400 hover:text-white transition-colors font-medium"
              >
                {link.name}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="inline-flex mt-4 btn-primary"
            >
              Get Consultation
            </a>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="pt-28 pb-16 sm:pt-32 sm:pb-20 lg:pt-40 lg:pb-32 relative overflow-hidden grid-pattern" data-testid="hero-section">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-emerald-500/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/4 right-1/4 w-48 sm:w-80 h-48 sm:h-80 bg-emerald-500/5 rounded-full blur-[100px]"></div>
      </div>

      <div className="section-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="animate-fadeInUp">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-3 sm:px-4 py-2 rounded-full mb-4 sm:mb-6">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              <span className="text-xs sm:text-sm font-semibold text-emerald-400 uppercase tracking-wider">
                Business Automation
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.1] text-white mb-4 sm:mb-6" data-testid="hero-headline">
              Automation Systems Built For{' '}
              <span className="gradient-text">Modern Businesses</span>
            </h1>

            <p className="text-base sm:text-lg text-gray-400 leading-relaxed mb-6 sm:mb-8 max-w-xl" data-testid="hero-subheadline">
              We help businesses reduce admin, automate operations, and improve customer workflows through custom-built systems and automation.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-12">
              <a
                href="#contact"
                className="btn-primary text-center justify-center text-sm sm:text-base"
                data-testid="hero-cta-primary"
              >
                Book a Consultation
                <ArrowRight size={18} />
              </a>
              <a
                href="#case-studies"
                className="btn-secondary text-center justify-center text-sm sm:text-base"
                data-testid="hero-cta-secondary"
              >
                <Play size={18} />
                View Live Systems
              </a>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="text-emerald-400 flex-shrink-0" size={14} />
                <span>Live Deployments</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="text-emerald-400 flex-shrink-0" size={14} />
                <span>Custom Built</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="text-emerald-400 flex-shrink-0" size={14} />
                <span>Real Workflows</span>
              </div>
            </div>
          </div>

          {/* Right Visual - Dashboard mockup */}
          <div className="animate-fadeInUp delay-200 relative mt-8 lg:mt-0">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#111827] shadow-2xl">
              {/* Dashboard header */}
              <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 border-b border-white/10">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500"></div>
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-500"></div>
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
                <span className="ml-2 sm:ml-4 text-[10px] sm:text-xs text-gray-500 truncate">dashboard.mushongasystems.com</span>
              </div>
              
              {/* Dashboard content */}
              <div className="p-3 sm:p-6">
                <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
                  {[
                    { label: 'Active Workflows', value: '24', change: '+12%' },
                    { label: 'Tasks Automated', value: '1,847', change: '+28%' },
                    { label: 'Time Saved', value: '156h', change: '+45%' }
                  ].map((stat, i) => (
                    <div key={i} className="bg-white/5 rounded-lg sm:rounded-xl p-2 sm:p-4 border border-white/5">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1 truncate">{stat.label}</p>
                      <p className="text-sm sm:text-xl font-bold text-white">{stat.value}</p>
                      <span className="text-[10px] sm:text-xs text-emerald-400">{stat.change}</span>
                    </div>
                  ))}
                </div>
                
                {/* Workflow visualization */}
                <div className="bg-white/5 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-white/5">
                  <div className="flex items-center justify-between mb-2 sm:mb-4">
                    <span className="text-xs sm:text-sm font-medium text-white">Customer Booking Flow</span>
                    <span className="text-[10px] sm:text-xs text-emerald-400 bg-emerald-500/10 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">Active</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-3 overflow-x-auto">
                    {['Enquiry', 'Confirm', 'Schedule', 'Complete'].map((step, i) => (
                      <React.Fragment key={i}>
                        <div className="flex-1 min-w-0 bg-emerald-500/20 rounded-md sm:rounded-lg p-1.5 sm:p-2 text-center">
                          <span className="text-[10px] sm:text-xs text-emerald-300 truncate block">{step}</span>
                        </div>
                        {i < 3 && <ChevronRight size={12} className="text-gray-600 flex-shrink-0 hidden sm:block" />}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating notification - hidden on very small screens */}
            <div className="hidden sm:flex absolute -bottom-4 -left-4 bg-[#1F2937] border border-white/10 rounded-xl p-3 sm:p-4 shadow-xl animate-pulse-glow">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                  <Bell className="text-emerald-400" size={16} />
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-medium text-white">New booking received</p>
                  <p className="text-[10px] sm:text-xs text-gray-500">Auto-confirmed • Just now</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: <Workflow size={24} />,
      title: 'Workflow Automation',
      description: 'Eliminate repetitive tasks and streamline your business processes with custom automation.',
      span: 'col-span-1 sm:col-span-2 md:col-span-2'
    },
    {
      icon: <Calendar size={24} />,
      title: 'Booking Systems',
      description: 'Customer-facing booking portals with automated confirmations and scheduling.',
      span: 'col-span-1'
    },
    {
      icon: <LayoutDashboard size={24} />,
      title: 'Admin Dashboards',
      description: 'Internal management portals to track, manage, and report on business operations.',
      span: 'col-span-1'
    },
    {
      icon: <Users size={24} />,
      title: 'CRM Integrations',
      description: 'Connect your customer data across platforms for unified business intelligence.',
      span: 'col-span-1'
    },
    {
      icon: <Bot size={24} />,
      title: 'AI Assistants',
      description: 'Intelligent automation for customer support, lead qualification, and data processing.',
      span: 'col-span-1'
    },
    {
      icon: <MessageSquare size={24} />,
      title: 'Customer Communication',
      description: 'Automated email, SMS, and notification systems for customer engagement.',
      span: 'col-span-1 sm:col-span-2 md:col-span-2'
    },
    {
      icon: <Wrench size={24} />,
      title: 'Internal Business Tools',
      description: 'Custom tools for inventory, task management, and operational efficiency.',
      span: 'col-span-1'
    },
    {
      icon: <Globe size={24} />,
      title: 'Web Development',
      description: 'Modern websites and web applications integrated with your business systems.',
      span: 'col-span-1'
    }
  ];

  return (
    <section id="services" className="py-16 sm:py-24 md:py-32 relative" data-testid="services-section">
      <div className="section-container">
        <div className="text-center mb-10 sm:mb-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 sm:mb-4 block">
            What We Build
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter text-white px-2">
            Systems That Drive{' '}
            <span className="gradient-text">Business Outcomes</span>
          </h2>
          <p className="text-gray-400 mt-3 sm:mt-4 max-w-2xl mx-auto text-sm sm:text-base px-4">
            We focus on building operational systems that save time, reduce manual work, and improve customer experience.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {services.map((service, index) => (
            <div 
              key={index}
              className={`${service.span} bg-[#111827] border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 card-glow group cursor-pointer`}
              data-testid={`service-${index}`}
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-500/10 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-emerald-500/20 transition-colors">
                <div className="text-emerald-400">
                  {service.icon}
                </div>
              </div>
              <h3 className="text-base sm:text-lg font-bold text-white mb-1 sm:mb-2">
                {service.title}
              </h3>
              <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Case Study Section
const CaseStudySection = () => {
  const features = [
    { icon: <Calendar size={16} />, text: 'Online booking portal' },
    { icon: <LayoutDashboard size={16} />, text: 'Admin management dashboard' },
    { icon: <Bell size={16} />, text: 'Automated notifications' },
    { icon: <Database size={16} />, text: 'Repair request tracking' },
    { icon: <Settings size={16} />, text: 'Workflow automation' },
    { icon: <Users size={16} />, text: 'Volunteer coordination' }
  ];

  return (
    <section id="case-studies" className="py-16 sm:py-24 md:py-32 bg-[#111827]/50" data-testid="case-study-section">
      <div className="section-container">
        <div className="text-center mb-10 sm:mb-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 sm:mb-4 block">
            Featured Case Study
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter text-white">
            Live System Deployment
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left - Image */}
          <div className="relative order-2 lg:order-1">
            <div className="relative flex items-center justify-center p-4 sm:p-8">
              <img
                src="https://customer-assets.emergentagent.com/job_process-boost-2/artifacts/0meyz3iq_Logo.png"
                alt="Peterborough Repair Café"
                className="w-full max-w-xs sm:max-w-sm h-auto"
              />
            </div>
            
            {/* Floating badge */}
            <div className="absolute right-0 sm:-right-4 top-4 sm:top-1/4 bg-[#1F2937] border border-emerald-500/30 rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-xl">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                <span className="text-emerald-400 text-xs sm:text-sm font-semibold">Live</span>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div className="order-1 lg:order-2">
            <div className="mb-4 sm:mb-6">
              <span className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-semibold px-3 py-1 rounded-full mb-3 sm:mb-4">
                LIVE PRODUCTION SYSTEM
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-white">Peterborough Repair Café UK</h3>
              <p className="text-gray-500 text-xs sm:text-sm mt-1">Community Charity Digital Transformation</p>
            </div>

            <div className="mb-6 sm:mb-8">
              <p className="text-gray-400 leading-relaxed text-sm sm:text-base">
                We transformed a manual repair request process into a fully digital workflow, giving volunteers, administrators and customers a single system to manage repair requests from submission through to completion.
              </p>
            </div>

            {/* Features grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
              {features.map((feature, i) => (
                <div key={i} className="flex items-center gap-2 sm:gap-3 text-gray-300">
                  <CheckCircle2 className="text-emerald-400 flex-shrink-0" size={14} />
                  <span className="text-xs sm:text-sm">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/case-study/repair-cafe"
                className="btn-primary justify-center text-sm"
                data-testid="case-study-link"
              >
                View Full Case Study
                <ArrowRight size={16} />
              </Link>
              <a
                href="https://peterboroughrepaircafe.uk/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary justify-center text-sm"
              >
                Visit Live Site
                <ExternalLink size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Reusable Systems Section
const SystemsSection = () => {
  const systems = [
    {
      title: 'Booking Workflows',
      description: 'Customer scheduling with automatic confirmations, reminders, and calendar sync.',
      icon: <Calendar size={18} />
    },
    {
      title: 'Customer Update Systems',
      description: 'Automated status updates and communication throughout service delivery.',
      icon: <MessageSquare size={18} />
    },
    {
      title: 'Enquiry Automation',
      description: 'Lead capture and qualification with intelligent routing and follow-up.',
      icon: <Zap size={18} />
    },
    {
      title: 'Admin Portals',
      description: 'Staff dashboards for managing operations, tracking metrics, and reporting.',
      icon: <LayoutDashboard size={18} />
    },
    {
      title: 'AI Chat Systems',
      description: 'Intelligent customer support and FAQ handling with human handoff.',
      icon: <Bot size={18} />
    },
    {
      title: 'Lead Follow-up',
      description: 'Automated sequences to nurture leads and convert enquiries to customers.',
      icon: <Users size={18} />
    }
  ];

  return (
    <section id="systems" className="py-16 sm:py-24 md:py-32" data-testid="systems-section">
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          {/* Left - Header */}
          <div className="lg:sticky lg:top-32">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 sm:mb-4 block">
              Reusable Systems
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tighter text-white mb-4 sm:mb-6">
              Production-Ready Business Systems
            </h2>
            <p className="text-gray-400 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">
              We've developed proven system templates that can be customized and deployed for your business. Each system is built on real-world requirements and tested in production environments.
            </p>
            <a href="#contact" className="btn-primary text-sm sm:text-base">
              Discuss Your Needs
              <ArrowRight size={16} />
            </a>
          </div>

          {/* Right - Systems grid */}
          <div className="grid gap-3 sm:gap-4 mt-8 lg:mt-0">
            {systems.map((system, index) => (
              <div 
                key={index}
                className="bg-[#111827] border border-white/10 rounded-lg sm:rounded-xl p-4 sm:p-5 card-glow flex items-start gap-3 sm:gap-4"
                data-testid={`system-${index}`}
              >
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <div className="text-emerald-400">{system.icon}</div>
                </div>
                <div className="min-w-0">
                  <h3 className="text-white font-semibold mb-0.5 sm:mb-1 text-sm sm:text-base">{system.title}</h3>
                  <p className="text-gray-500 text-xs sm:text-sm">{system.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Trust Section
const TrustSection = () => {
  const stats = [
    { value: 'Live', label: 'Production Systems', icon: <Zap size={18} /> },
    { value: 'Custom', label: 'Built Solutions', icon: <Wrench size={18} /> },
    { value: 'Real', label: 'Business Workflows', icon: <Workflow size={18} /> },
    { value: 'Secure', label: 'Data Handling', icon: <Shield size={18} /> }
  ];

  return (
    <section className="py-10 sm:py-16 border-y border-white/10" data-testid="trust-section">
      <div className="section-container">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-500/10 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-2 sm:mb-3">
                <div className="text-emerald-400">{stat.icon}</div>
              </div>
              <p className="text-lg sm:text-2xl font-bold text-white mb-0.5 sm:mb-1">{stat.value}</p>
              <p className="text-xs sm:text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message);
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-24 md:py-32 relative" data-testid="contact-section">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] bg-emerald-500/5 rounded-full blur-[120px]"></div>
      </div>

      <div className="section-container relative z-10">
        <div className="text-center mb-10 sm:mb-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 sm:mb-4 block">
            Get Started
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter text-white px-2">
            Let's Build Your System
          </h2>
          <p className="text-gray-400 mt-3 sm:mt-4 max-w-2xl mx-auto text-sm sm:text-base px-4">
            Book a consultation to discuss your business needs and explore how automation can improve your operations.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <form 
            onSubmit={handleSubmit}
            className="bg-[#111827] border border-white/10 rounded-xl sm:rounded-2xl p-5 sm:p-8 md:p-10 tracing-beam"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="input-field text-sm sm:text-base"
                  placeholder="Your name"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="input-field text-sm sm:text-base"
                  placeholder="you@company.com"
                  data-testid="contact-email"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input-field text-sm sm:text-base"
                  placeholder="+44 7712 345678"
                  data-testid="contact-phone"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="input-field text-sm sm:text-base"
                  placeholder="Your company"
                  data-testid="contact-company"
                />
              </div>
            </div>

            <div className="mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">
                What would you like to automate? *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                className="textarea-field text-sm sm:text-base"
                placeholder="Tell us about your business and the processes you'd like to improve..."
                rows={4}
                data-testid="contact-message"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              data-testid="contact-submit"
            >
              {isSubmitting ? 'Sending...' : (
                <>
                  Book Consultation
                  <Send size={16} />
                </>
              )}
            </button>
          </form>

          {/* Contact info */}
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 sm:gap-8 mt-8 sm:mt-10">
            <a href="mailto:adam@mushongasystems.com" className="flex items-center justify-center gap-2 text-gray-500 hover:text-white transition-colors text-sm">
              <Mail size={16} />
              <span className="truncate">adam@mushongasystems.com</span>
            </a>
            <a href="tel:+447539249987" className="flex items-center justify-center gap-2 text-gray-500 hover:text-white transition-colors text-sm">
              <Phone size={16} />
              +44 7539 249987
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-8 sm:py-12 border-t border-white/10" data-testid="footer">
      <div className="section-container">
        <div className="flex flex-col items-center gap-6 md:flex-row md:justify-between">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="text-white font-bold text-sm sm:text-base">Mushonga Systems</span>
          </div>
          
          <div className="text-center">
            <p className="text-gray-500 text-xs sm:text-sm">
              © {new Date().getFullYear()} Mushonga Systems. All rights reserved.
            </p>
            <p className="text-gray-600 text-[10px] sm:text-xs mt-1">
              Mushonga Systems is a trading name of ATM29 LTD. Registered in England and Wales. Company No. 13457210.
            </p>
          </div>

          <div className="flex items-center gap-4 sm:gap-6">
            <a href="#services" className="text-gray-500 hover:text-white text-xs sm:text-sm transition-colors">Services</a>
            <a href="#case-studies" className="text-gray-500 hover:text-white text-xs sm:text-sm transition-colors">Case Studies</a>
            <a href="#contact" className="text-gray-500 hover:text-white text-xs sm:text-sm transition-colors">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="landing-page">
      <Header />
      <main>
        <HeroSection />
        <ServicesSection />
        <CaseStudySection />
        <SystemsSection />
        <TrustSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
